/**
 * The composition point. Adding a page means touching one branch file, never this one.
 *
 * Two shells, two guards: `PublicOnlyRoute` wraps sign-in and unlock and bounces an
 * authenticated user onward; `ProtectedRoute` wraps everything else, sends an anonymous one to
 * sign-in, and preserves the attempted URL so they land where they were going.
 */

import { createBrowserRouter, type RouteObject } from 'react-router';

import { RootErrorBoundary } from '@/pages/errors/RootErrorBoundary';
import { ShellErrorBoundary } from '@/pages/errors/ShellErrorBoundary';
import { registerPrefetch } from '@/shared/lib/prefetch';

import { appRoutes, prefetch } from './app.routes.js';
import { errorRoutes } from './error.routes.js';
import { publicRoutes } from './public.routes.js';
import { PermissionRoute } from './guards/PermissionRoute.js';
import { ProtectedRoute } from './guards/ProtectedRoute.js';
import { PublicOnlyRoute } from './guards/PublicOnlyRoute.js';

const routes: RouteObject[] = [
  {
    path: '/',
    // Root boundary: a throw anywhere unmatched lands here rather than on a blank page.
    ErrorBoundary: RootErrorBoundary,
    children: [
      { Component: PublicOnlyRoute, children: publicRoutes },

      {
        Component: ProtectedRoute,
        // Per-branch override, so a throw inside the app still renders inside the shell — the
        // user keeps their navigation and their context instead of being dropped somewhere new.
        ErrorBoundary: ShellErrorBoundary,
        children: [{ Component: PermissionRoute, children: appRoutes }],
      },

      ...errorRoutes,
    ],
  },
];

// Registered here, where the imports are allowed to live. Features warm a chunk by name.
for (const [name, loader] of Object.entries(prefetch)) registerPrefetch(name, loader);

export const router = createBrowserRouter(routes);
export type { RouteHandle } from './guards/PermissionRoute.js';
